import { OrderEstimateStatus, OrderTermResponse } from '@business/entities/order';
import { InvoiceDiscountType, InvoiceSettingsResponse, TransactionInvoiceResponse } from '@business/entities/TransactionInvoice';
import { SettingsConfig } from '@core/utils/config';
import { R } from '@core/utils/r';
import { toMoment } from '@core/utils/time';
import { OrderShipmentStatus } from './../../entities/order/OrderShipmentResponse';
import {
  TimeMaterialStatus,
  TMStageType,
  TimeMaterialShippingOption,
  TimeMaterialResponse,
  CompactTimeMaterialResponse,
  ProductStatus,
  TMChangeOrderStatus,
  EstimateLineItem,
  TMFeeType,
  RangValue,
} from '@business/entities/tm';
import { TimeMaterialShipmentStatus } from '@business/entities/tm/TimeMaterialShipmentResponse';
import { CompactTimeMaterialRMAResponse, TimeMaterialRMAResponse } from '@business/entities/tm/TimeMaterialRMAResponse';
import { PriceFeeFormData } from '@modules/proposal/ProposalBuilderPage/Components/ProposalProductsOverlay/ProposalProductsOverlay';
import { OrderLineItemData } from '@modules/timeMaterials/AddEditTimeMaterialPage/TimeMaterialForm';
import { ProposalService } from '../proposal_service';
import { ProposalDiscountFormData } from '@modules/proposal/ProposalBuilderPage/Components/ProposalProductsOverlay';
import { ProposalDiscountCategory } from '@business/entities/proposal';
import { EstimatorTemplateStatus, EstimatorTemplateType, SelectEstimateType } from '@business/entities/tm/TmTemplateEstimatorResponse';
import { PaymentType } from '@business/entities/tm/TmRegionResponse';
import { TmInvoiceScheduleStatus } from '@business/entities/tm/TMInvoiceScheduleResponse';
import { OrgAddressResponse } from '@business/entities/organization';
import { VeryCompactWorkOrderResponse, JobTypeId, RegionId } from '@business/entities/tm';

export function getStatusTitle(status: TimeMaterialStatus | OrderEstimateStatus | TimeMaterialShipmentStatus) {
  switch (status) {
    case TimeMaterialStatus.open:
      return 'Open';
    case TimeMaterialStatus.completed:
      return 'Completed';
    case TimeMaterialStatus.canceled:
      return 'Canceled';
    case TimeMaterialShipmentStatus.acknowledged:
      return 'Acknowledged';
    case TimeMaterialShipmentStatus.shipped:
      return 'Shipped';
    case TimeMaterialShipmentStatus.delivered:
      return 'Delivered';
    case TimeMaterialShipmentStatus.opened:
      return 'Opened';
  }
  return 'Unknown';
}

export function allStatuses() {
  return [TimeMaterialStatus.open, TimeMaterialStatus.completed, TimeMaterialStatus.canceled];
}

export function partStatuses() {
  const data = [
    ProductStatus.pending,
    ProductStatus.ordered,
    ProductStatus.partial,
    ProductStatus.partialInstalled,
    ProductStatus.received,
    ProductStatus.returned,
    ProductStatus.installed,
    ProductStatus.delivered,
  ];
  return data;
}

export function getPartStatusLabel(status: ProductStatus) {
  switch (status) {
    case ProductStatus.pending:
      return 'Pending';
    case ProductStatus.ordered:
      return 'Ordered';
    case ProductStatus.partial:
      return 'Partial';
    case ProductStatus.partialInstalled:
      return 'Partial Installed';
    case ProductStatus.received:
      return 'Received';
    case ProductStatus.returned:
      return 'Returned';
    case ProductStatus.installed:
      return 'Installed';
    case ProductStatus.delivered:
      return 'Delivered';
  }
}

export function hasShippingInfo(type: TMStageType) {
  return R.includes([TMStageType.acknowledge, TMStageType.delivered, TMStageType.shipped], type);
}
export function allShippingOptions() {
  return [TimeMaterialShippingOption.ready, TimeMaterialShippingOption.complete];
}

export function getShippingOptionTitle(status: TimeMaterialShippingOption) {
  switch (status) {
    case TimeMaterialShippingOption.ready:
      return 'Ship as Ready';
    case TimeMaterialShippingOption.complete:
      return 'Ship as Complete';
  }
  return 'Unknown';
}

export function getShipmentStatusTitle(status: TimeMaterialShipmentStatus) {
  switch (status) {
    case TimeMaterialShipmentStatus.acknowledged:
      return 'Acknowledged';
    case TimeMaterialShipmentStatus.shipped:
      return 'Shipped';
    case TimeMaterialShipmentStatus.opened:
      return 'Opened';
    case TimeMaterialShipmentStatus.delivered:
      return 'Delivered';
  }
  return 'Unknown';
}

export function shipmentStatuses() {
  return [
    TimeMaterialShipmentStatus.opened,
    TimeMaterialShipmentStatus.acknowledged,
    TimeMaterialShipmentStatus.shipped,
    TimeMaterialShipmentStatus.delivered,
  ];
}

export function canCompleteIt(order: TimeMaterialResponse | CompactTimeMaterialResponse, lines: string[] = []) {
  const poItems = R.flatMap(order.purchaseOrders, po => po.items).filter(
    i => R.includes(lines, i.id.toString()) && i.status !== OrderShipmentStatus.delivered,
  );
  if (order.stage.type === TMStageType.delivered) {
    return poItems.length === 0;
  }
  return false;
}

export function getStatusClass(status: TimeMaterialStatus) {
  switch (status) {
    case TimeMaterialStatus.open:
      return 'badge-soft-primary';
    case TimeMaterialStatus.completed:
      return 'badge-soft-success';
    case TimeMaterialStatus.canceled:
      return 'badge-soft-danger';
  }
}

export function getItemStatusClass(status: string) {
  switch (status) {
    case 'closed' || 'open':
      return 'badge-primary';
    case 'accepted':
      return 'badge-success';
    case 'rejected':
      return 'badge-danger';
    case 'pending':
      return 'badge-warning';
  }
}

export function getApprovalActionTitle(status: string) {
  switch (status) {
    case 'noCreateInvoice':
      return "Don't create invoice";
    case 'createInvoice':
      return 'Create and Save invoice';
    case 'createSendInvoice':
      return 'Create and Send invoice';
  }
  return status;
}

export function isUMEnabled() {
  const orderSetting = SettingsConfig?.current()?.order;
  if (orderSetting?.isUMEnabled) {
    return true;
  }

  return false;
}

export function isNameEnabled() {
  const orderSetting = SettingsConfig?.current()?.order;
  if (orderSetting?.isUMEnabled) {
    return true;
  }
  return false;
}

export function getSettingAddressString(address: InvoiceSettingsResponse, joinBy = '\n') {
  return R.compact([address.address, `${address.city}, ${address.state} ${address.postal}`, address.country, address.phone]).join(joinBy);
}

export function getDueDateByTerm(term: OrderTermResponse, date: number) {
  let momentDate = toMoment(date).add(term?.fixedDays || 0, 'days');
  if (term?.dayOfMonth && term.dayOfMonth > 0) {
    momentDate = momentDate.set('date', term.dayOfMonth === 31 ? momentDate.daysInMonth() : term.dayOfMonth);
  }

  if (term?.dayOfDueDate && term?.dayOfMonth && term.dayOfDueDate > 0 && momentDate.diff(toMoment(date), 'days') < term.dayOfDueDate) {
    momentDate = momentDate.add(term?.dayOfDueDate || 0, 'days');
    momentDate = momentDate.set('date', term?.dayOfMonth);
  }

  return momentDate.unix();
}

export function partStatusColor(status: ProductStatus) {
  switch (status) {
    case ProductStatus.returned:
      return 'rgba(244, 106, 106, 0.25)';
    case ProductStatus.partial:
      return 'rgba(116, 120, 141, 0.25)';
    case ProductStatus.ordered:
      return 'rgba(85, 110, 230, 0.25)';
    case ProductStatus.installed:
      return 'rgba(52, 195, 143, 0.25)';
    default:
      return '';
  }
}

export function getCOStatusClass(status: TMChangeOrderStatus) {
  switch (status) {
    case TMChangeOrderStatus.open:
      return 'badge-soft-warning';
    case TMChangeOrderStatus.sent:
      return 'badge-soft-primary';
    case TMChangeOrderStatus.approved:
      return 'badge-soft-success';
    case TMChangeOrderStatus.paid:
      return 'badge-soft-success';
    case TMChangeOrderStatus.pending:
      return 'badge-soft-warning';
    case TMChangeOrderStatus.declined:
      return 'badge-soft-danger';
  }
}

export function getCOStatusTitle(status: TMChangeOrderStatus) {
  switch (status) {
    case TMChangeOrderStatus.open:
      return 'Draft';
    case TMChangeOrderStatus.sent:
      return 'Sent';
    case TMChangeOrderStatus.approved:
      return 'Approved';
    case TMChangeOrderStatus.paid:
      return 'Paid';
    case TMChangeOrderStatus.pending:
      return 'Under Review';
    case TMChangeOrderStatus.declined:
      return 'Declined';
  }
}

export function shouldShowRMAItem(rmas: (CompactTimeMaterialRMAResponse | TimeMaterialRMAResponse)[], product: EstimateLineItem) {
  const productQuantity = product.quantity;
  const rmaTotal = R.filter(rmas, rma => rma.item == (product.id as any));
  return productQuantity > rmaTotal.length;
}

export function getFeeTotal(items: PriceFeeFormData[]) {
  const checkedItems = items.filter(item => item.isChecked || (item.isChecked as any) == 'true');
  const t = checkedItems.reduce((acc, item) => acc + R.toNumber(item.percent), 0);
  return t;
}

export function getFeeTotalAmount(items: PriceFeeFormData[], lineItems: OrderLineItemData[]) {
  const checkedItems = items.filter(item => item.isChecked || (item.isChecked as any) == 'true');
  const t = checkedItems.reduce((acc, item) => {
    const subTot = lineItems.reduce((car, o) => car + (ProposalService.getSubTotalAddProd(o, false, item.feeType) as number), 0);
    const fTotal = (subTot / 100) * item.percent;
    return acc + fTotal;
  }, 0);
  return t;
}

export function calculateTotalSalesTax(items: OrderLineItemData[], taxRate: number, fees: PriceFeeFormData[]) {
  const t = items.filter(i => i.taxable).reduce((acc, item) => acc + (ProposalService.getSubTotalAddProd(item, false) as number), 0);
  const taxableFeeTotalPercentage = fees.filter(i => i.isChecked && i.taxable).reduce((acc, item) => acc + item.percent, 0);
  const taxableFeeTotal = (t / 100) * taxableFeeTotalPercentage;
  const newT = t + taxableFeeTotal;
  return R.roundDecimal((newT / 100) * taxRate, 2);
}

export function tmFeeItems() {
  return [TMFeeType.item, TMFeeType.itemandlabor];
}

export function getTmFeeTypeLabel(type: TMFeeType) {
  switch (type) {
    case TMFeeType.item:
      return 'Items Only';
    case TMFeeType.itemandlabor:
      return 'Items & Labor';
  }
}

export function getOrderGTotal(order: TimeMaterialResponse) {
  const estimate = order.estimate;
  const laborPartsTotal = estimate.subTotal;
  const discount = getOrderDiscount(order);
  const salesTax = orderSalesTax(order);
  return laborPartsTotal + salesTax - discount;
}

export function getOrderDiscount(order: TimeMaterialResponse) {
  const estimate = order.estimate;
  const orderEstimateDiscount = estimate.discount as ProposalDiscountFormData;
  const discountValue = orderEstimateDiscount.value;
  const isDiscountByLabor = orderEstimateDiscount.category == ProposalDiscountCategory.labor;
  const isDiscountByItems = orderEstimateDiscount.category == ProposalDiscountCategory.items;

  if (!estimate.discount.isDiscount) return 0;

  if (estimate.discount.type === InvoiceDiscountType.fixed) {
    return estimate.discount.value;
  } else {
    if (isDiscountByLabor) {
      return R.toNumber(ProposalService.getDiscountAmount(orderEstimateDiscount, order.estimate.labourPrice));
    }
    if (isDiscountByItems) {
      return R.toNumber(ProposalService.getDiscountAmount(orderEstimateDiscount, order.estimate.partsPrice));
    }
    return R.toNumber((order.estimate.totalAmount * discountValue) / 100);
  }
}

/** Work Order invoice titles by id, as the Client Invoices list shows them ("Invoice# 2"). */
export function getInvoiceTitles(invoices: TransactionInvoiceResponse[]): Record<string, string> {
  const titles: Record<string, string> = {};
  (invoices ?? []).forEach((invoice, index) => {
    titles[String(invoice.id)] = invoice.title || `Invoice# ${index + 1}`;
  });
  return titles;
}

export function orderSalesTax(order: TimeMaterialResponse) {
  const estimate = order.estimate;
  if (estimate.salesTax.taxAmount && estimate.salesTax.taxAmount > 0) {
    return estimate.salesTax.taxAmount;
  }
  if (estimate.salesTax.value === 0) return 0;
  const taxTotal = estimate.items
    .filter(l => l.taxable === true || l.packageItems?.some(p => p.taxable === true))
    .reduce((total, line) => total + (ProposalService.getSubTotalEstimate(line, false, false, true) as number), 0);
  return (estimate.salesTax.value * taxTotal) / 100;
}

export function getRangeTitle(value: RangValue) {
  switch (value) {
    case RangValue.squareFeet:
      return '$/SF';
    case RangValue.total:
      return 'Total';
  }
  return 'Unknown';
}

export function getSelectEstimateTitle(value: SelectEstimateType) {
  switch (value) {
    case SelectEstimateType.group:
      return 'Group';
    case SelectEstimateType.category:
      return 'Category';
  }
  return 'Unknown';
}
export function getEstimateStatusTitle(value: EstimatorTemplateStatus) {
  switch (value) {
    case EstimatorTemplateStatus.active:
      return 'Active';
    case EstimatorTemplateStatus.deactivate:
      return 'Inactive';
  }
  return 'Unknown';
}

export function getPaymentTypeStatusTitle(value: PaymentType | 'all') {
  switch (value) {
    case PaymentType.weekly:
      return 'Weekly';
    case PaymentType.monthly:
      return 'Monthly';
    case PaymentType.vcc:
      return 'Vcc';
    case PaymentType.credit_card:
      return 'Credit Card';
    case PaymentType.mid_mos:
      return 'Mid Mos';
    case PaymentType.other:
      return 'Other';
    case 'all':
      return 'All';
  }
  return 'Unknown';
}

export function getBadgeColor(value: EstimatorTemplateStatus) {
  switch (value) {
    case EstimatorTemplateStatus.active:
      return 'info';
    case EstimatorTemplateStatus.deactivate:
      return 'danger';
  }
}

export function getInvoiceStatusTitle(value: TmInvoiceScheduleStatus) {
  switch (value) {
    case TmInvoiceScheduleStatus.sent:
      return 'Sent';
    case TmInvoiceScheduleStatus.dueSoon:
      return 'Due Soon';
    case TmInvoiceScheduleStatus.overDue:
      return 'Over Due';
    case TmInvoiceScheduleStatus.paid:
      return 'Paid';
  }
  return '';
}

export function getTypeBadgeColor(value: EstimatorTemplateType) {
  switch (value) {
    case EstimatorTemplateType.main:
      return 'primary';
    case EstimatorTemplateType.subproject:
      return 'info';
  }
  return 'unknown';
}

export function getFullAddress(address: OrgAddressResponse) {
  return `${address.address}${address.addressLineTwo ? ', ' + address.addressLineTwo : ''}, ${address.city}, ${address.state} ${address.postal}`;
}

/**
 * Search and filter work orders based on search text, job type, and region
 */
export interface WorkOrderFilterOptions {
  search?: string;
  jobTypeId?: JobTypeId | null;
  regionId?: RegionId | null;
}

export function filterWorkOrders(
  workOrders: VeryCompactWorkOrderResponse[],
  options: {
    search?: string;
    jobTypeId?: JobTypeId | null;
    regionId?: RegionId | null;
  },
): VeryCompactWorkOrderResponse[] {
  let filtered = [...workOrders];

  if (options.search != '' && options.search && options.search.trim()) {
    const search = options.search.toLowerCase().trim();
    filtered = filtered.filter(workOrder => {
      const contactName = `${workOrder.contact?.firstName || ''} ${workOrder.contact?.lastName || ''}`.toLowerCase();

      const address = workOrder.billingAddress
        ? `${workOrder.billingAddress.address || ''} ${workOrder.billingAddress.addressLineTwo || ''} ${workOrder.billingAddress.city || ''} ${
            workOrder.billingAddress.state || ''
          } ${workOrder.billingAddress.postal || ''}`.toLowerCase()
        : '';

      const region = workOrder.billingAddress?.region?.name?.toLowerCase() || '';

      return contactName.includes(search) || address.includes(search) || region.includes(search);
    });
  }

  // Filter by job type
  if (options.jobTypeId !== null && options.jobTypeId !== undefined) {
    filtered = filtered.filter(workOrder => {
      const workOrderJobTypeId = workOrder.jobType?.id;

      if (!workOrderJobTypeId) {
        return false;
      }

      return workOrderJobTypeId == options.jobTypeId;
    });
  }

  if (options.regionId !== null && options.regionId !== undefined) {
    filtered = filtered.filter(workOrder => {
      const workOrderRegionId = workOrder.billingAddress?.region?.id;
      if (!workOrderRegionId) {
        return false;
      }
      return workOrderRegionId == options.regionId;
    });
  }

  return filtered;
}
